<template>
  <v-row>
    <v-col cols="12">
      <v-form ref="crudForm" @submit.prevent="submit">

        <!-- Basic Data -->
        <v-row>
          <v-col cols="12" md="4" order-md="last">
            <MediaSelector directory="Provider" v-model="model.PhotoId" :disabled="disableAll" />
          </v-col>
          <v-spacer />
          <v-col cols="12" md="7" order-md="first">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field label="First Name"
                              v-model="model.FirstName"
                              :disabled="disableAll"
                              :rules="rules.FirstName" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field label="Last Name"
                              v-model="model.LastName"
                              :disabled="disableAll"
                              :rules="rules.LastName" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field label="Preferred Name"
                              :disabled="disableAll"
                              v-model="model.PrefName" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field label="Title"
                              v-model="model.Title"
                              :disabled="disableAll"
                              :rules="rules.Title" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field label="Year Joined"
                              :disabled="disableAll"
                              :rules="rules.Year"
                              v-model="model.YearJoined"
                              v-mask="masks.yearRange" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field label="NPI"
                              :disabled="disableAll"
                              v-model="model.Npi" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field label="Provider Code"
                              :disabled="disableAll"
                              v-model="model.ProviderCode" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field label="Vimeo ID"
                              :disabled="disableAll"
                              v-model="model.VimeoId" />
              </v-col>
              <v-col cols="12">
                <v-textarea label="Comments"
                            v-model="model.Comments"
                            outlined
                            :disabled="disableAll" />
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="model.IndependentPractitioner"
                            :disabled="disableAll">
                  <template v-slot:label>
                    Independent Practitioner?
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2"
                                dense
                                v-on="on">help</v-icon>
                      </template>
                      Check this box if this provider is an independent practitioner
                      not associated with Carle Health.<br>
                      Leave unchecked if a Carle Health provider.
                    </v-tooltip>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="model.Resident"
                            :disabled="disableAll">
                  <template v-slot:label>
                    Resident?
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2"
                                dense
                                v-on="on">help</v-icon>
                      </template>
                      Check this box if this provider is a resident..<br>
                      Leave unchecked if not a resident.
                    </v-tooltip>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="model.HasStars"
                            :disabled="disableAll">
                  <template v-slot:label>
                    Enable Ratings
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2"
                                dense
                                v-on="on">help</v-icon>
                      </template>
                      Displays this provider's ratings on Carle.org.<br>
                      Leave unchecked to hide these ratings.
                    </v-tooltip>
                  </template>
                </v-checkbox>
                <v-checkbox v-model="model.Searchable"
                            :disabled="disableAll">
                  <template v-slot:label>
                    Show in Find a Doctor
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2"
                                dense
                                v-on="on">help</v-icon>
                      </template>
                      Displays this provider on the Carle.org Find a Doctor page.<br>
                      Leave unchecked to hide this doctor from general search.
                    </v-tooltip>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <!-- Tagging -->
          <v-col cols="12" md="4" order-md="last">

            <v-row class="fap-tags fap-tags--pronouns">
              <v-col cols="12">
                <h2>Preferred Pronouns</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Pronouns"
                            v-model="model.Pronouns"
                            :items="tags.items.Pronouns"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip close
                            v-bind="data.attrs"
                            @click:close="removeItem(model.Pronouns, data.item)">
                      <v-avatar left @click.stop="moveToFront(model.Pronouns, data.item)">
                        <v-icon>{{ data.index === 0 ? 'star' : 'star_border' }}</v-icon>
                      </v-avatar>
                      <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--affiliations">
              <v-col cols="12">
                <h2>Affiliations</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Affiliations"
                            v-model="model.Affiliations"
                            :items="tags.items.Affiliation"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                    <template v-slot:selection="data">
                        <draggable v-model="model.Affiliations" group="Affiliations"
                        ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                        :id="data.index" @change="changeOrder(model.Affiliations)"
                        class="v-drag">
                          <v-chip close
                                  draggable
                                  v-bind="data.attrs"
                                  :key="data.index"
                                  v-model="model.Affiliations[data.index]"
                                  @mousedown.stop
                                  @click:close="removeItem(model.Affiliations, data.item)">
                            <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                          </v-chip>
                        </draggable>
                    </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--certifications">
              <v-col cols="12">
                <h2>Certifications</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Certifications"
                            v-model="model.Certifications"
                            :items="tags.items.Certification"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <draggable v-model="model.Certifications" group="Certifications"
                    ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                    :id="data.index" @change="changeOrder(model.Certifications)"
                    class="v-drag">
                      <v-chip close
                              draggable
                              v-bind="data.attrs"
                              :key="data.index"
                              v-model="model.Certifications[data.index]"
                              @mousedown.stop
                              @click:close="removeItem(model.Certifications, data.item)">
                        <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                      </v-chip>
                    </draggable>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--departments">
              <v-col cols="12">
                <h2>Departments</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Departments"
                            v-model="model.Departments"
                            :items="tags.items.Department"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <draggable v-model="model.Departments" group="Departments"
                    ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                    :id="data.index" @change="changeOrder(model.Departments)"
                    class="v-drag">
                      <v-chip close
                              draggable
                              v-bind="data.attrs"
                              :key="data.index"
                              v-model="model.Departments[data.index]"
                              @mousedown.stop
                              @click:close="removeItem(model.Departments, data.item)">
                        <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                      </v-chip>
                    </draggable>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--medical-interests">
              <v-col cols="12">
                <h2>Medical Interests</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Medical Interests"
                            v-model="model.MedicalInterests"
                            :items="tags.items['Medical Interest']"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <draggable v-model="model.MedicalInterests" group="MedicalInterests"
                    ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                    :id="data.index" @change="changeOrder(model.MedicalInterests)"
                    class="v-drag">
                      <v-chip close
                              draggable
                              v-bind="data.attrs"
                              :key="data.index"
                              v-model="model.MedicalInterests[data.index]"
                              @mousedown.stop
                              @click:close="removeItem(model.MedicalInterests, data.item)">
                        <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                      </v-chip>
                    </draggable>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--research-departments">
              <v-col cols="12">
                <h2>Research Departments</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Research Departments"
                            v-model="model.ResearchDepartments"
                            :items="tags.items['Research Department']"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <draggable v-model="model.ResearchDepartments" group="ResearchDepartments"
                    ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                    :id="data.index" @change="changeOrder(model.ResearchDepartments)"
                    class="v-drag">
                      <v-chip close
                              draggable
                              v-bind="data.attrs"
                              :key="data.index"
                              v-model="model.ResearchDepartments[data.index]"
                              @mousedown.stop
                              @click:close="removeItem(model.ResearchDepartments, data.item)">
                        <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                      </v-chip>
                    </draggable>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--research-interests">
              <v-col cols="12">
                <h2>Research Interests</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Research Interests"
                            v-model="model.ResearchInterests"
                            :items="tags.items['Research Interest']"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <draggable v-model="model.ResearchInterests" group="ResearchInterests"
                    ghost-class="ghost" :sort="false" :key="data.index" :move="move"
                    :id="data.index" @change="changeOrder(model.ResearchInterests)"
                    class="v-drag">
                      <v-chip close
                              draggable
                              v-bind="data.attrs"
                              :key="data.index"
                              v-model="model.ResearchInterests[data.index]"
                              @mousedown.stop
                              @click:close="removeItem(model.ResearchInterests, data.item)">
                        <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                      </v-chip>
                    </draggable>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--specialties">
              <v-col cols="12">
                <h2>Specialties</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Specialties"
                            v-model="model.Specialties"
                            :items="tags.items.Specialty"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip close
                            v-bind="data.attrs"
                            @click:close="removeItem(model.Specialties, data.item)">
                      <v-avatar left @click.stop="moveToFront(model.Specialties, data.item)">
                        <v-icon>{{ data.index === 0 ? 'star' : 'star_border' }}</v-icon>
                      </v-avatar>
                      <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--languages">
              <v-col cols="12">
                <h2>Other Languages Spoken</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Languages"
                            v-model="model.Languages"
                            :items="tags.items.Language"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip close
                            v-bind="data.attrs"
                            @click:close="removeItem(model.Languages, data.item)">
                      <v-avatar left @click.stop="moveToFront(model.Languages, data.item)">
                        <v-icon>{{ data.index === 0 ? 'star' : 'star_border' }}</v-icon>
                      </v-avatar>
                      <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--additional-recognitions">
              <v-col cols="12">
                <h2>Additional Recognitions</h2>
              </v-col>
              <v-col cols="12">
                <v-textarea label="Additional Recognitions"
                              :disabled="disableAll"
                              outlined
                              v-model="model.AdditionalRecognitionsSingle" />
              </v-col>
            </v-row>
            <v-row class="fap-tags fap-tags--FacultyAppts">
              <v-col cols="12">
                <h2>Faculty Appts</h2>
              </v-col>
              <v-col cols="12">
                <v-combobox multiple chips
                            deletable-chips
                            label="Faculty Appts"
                            v-model="model.FacultyAppts"
                            :items="tags.items['Faculty Appointments']"
                            :disabled="disableAll">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip close
                            v-bind="data.attrs"
                            @click:close="removeItem(model.FacultyAppts, data.item)">
                      <v-avatar left @click.stop="moveToFront(model.FacultyAppts, data.item)">
                        <v-icon>{{ data.index === 0 ? 'star' : 'star_border' }}</v-icon>
                      </v-avatar>
                      <span class="text-wrapped text-wrapped--ellipsis">{{ data.item }}</span>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="8" order-md="first">

            <!-- Locations -->
            <v-row no-gutters>
              <v-col cols="12">
                <h2>Provider Locations</h2>
              </v-col>
              <v-col cols="12">
                <v-checkbox v-model="model.NoLocations"
                            :disabled="disableAll">
                  <template v-slot:label>
                    Disable Locations
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2"
                                dense
                                v-on="on">help</v-icon>
                      </template>
                      Check this box if this provider does not have a
                      physical location (Virtual Health).<br>
                      Leave unchecked if Provider has any phyical locations.
                    </v-tooltip>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-autocomplete multiple chips
                                deletable-chips
                                return-object
                                label="Locations"
                                item-text="Title"
                                item-value="Id"
                                ref="locationsMenu"
                                v-model="locations.selected"
                                :loading="locations.loading"
                                :items="locations.items"
                                :disabled="disableAll"
                                @keydown.enter.native.prevent="">
                  <template v-slot:append>
                    <v-btn color="accent" text small>
                      SELECT <v-icon dense right>add</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip close
                            color="primary"
                            v-bind="data.attrs"
                            :outlined="data.item.Id !== locations.primary"
                            :input-value="data.selected"
                            @click.stop="highlightLocation({
                              id: data.item.Id,
                              index: data.index
                            })"
                            @click:close="removeLocation(data.item)">
                      <v-avatar left>
                        <v-img :src="mediaUrl(data.item.Id, 'Location', 'Location.jpg')" />
                      </v-avatar>
                      {{ data.item.Title }}
                    </v-chip>
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-avatar>
                      <v-img :src="mediaUrl(data.item.Id, 'Location', 'Location.jpg')" />
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.Title }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="data.item.Phone">
                        Location Phone: {{ data.item.Phone }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" v-if="model.Locations.length">
                <v-expansion-panels accordion v-model="locations.highlighted.index">
                  <v-expansion-panel v-for="loc in model.Locations"
                                     :key="`location-${loc.Id}`">
                    <v-expansion-panel-header :class="{
                        'font-weight-bold': loc.Id === locations.primary
                      }">
                      {{ loc.Title }}
                      {{ loc.Id === locations.primary ? ' - Primary' : '' }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-col cols="12">
                        <v-text-field label="Provider Phone"
                                      v-model="loc.Phone"
                                      v-mask="masks.phone"
                                      :disabled="disableAll" />
                        <v-checkbox label="Primary?"
                                    v-model="locations.primary"
                                    :value="loc.Id"
                                    :disabled="disableAll || model.Locations.length === 1" />
                        <v-text-field label="Secondary Phone"
                                      v-model="loc.SecondaryPhone"
                                      v-mask="masks.phone"
                                      :disabled="disableAll" />
                        <v-text-field label="Address Info"
                                      v-model="loc.AddressAdd"
                                      :disabled="disableAll" />
                      </v-col>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Training and History -->
            <v-row no-gutters>
              <v-col cols="12">
                <v-row>
                  <v-col cols="12">
                    <h2>Training and History</h2>
                  </v-col>
                </v-row>
                <v-expansion-panels multiple>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Education
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="education"
                                v-model="model.Education"
                                :model="getHistoryModel()"
                                :disabled="disableAll">
                        <template v-slot:item="{ item, index }">
                          <v-col cols="12">
                            <v-autocomplete v-model="item.Institute"
                                            label="School"
                                            item-text="Title"
                                            item-value="Id"
                                            return-object
                                            :items="places.items"
                                            :disabled="disableAll"
                                            @keydown.enter.native.prevent="">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                              <template v-slot:prepend-item>
                                <v-list-item v-if="$store.getters.can('Editor')"
                                             ripple
                                             @click="openPlaceCreator(
                                           'Education', 'Institute', index
                                         )">
                                  <v-list-item-icon>
                                    <v-icon>add</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    Create New...
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                              </template>
                              <template v-slot:item="data">
                                <v-list-item-content>
                                  <v-list-item-title>
                                    {{ data.item.Title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    {{ getAddress(data.item) }}
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-combobox label="Degree"
                                        :items="tags.items.Education"
                                        :disabled="disableAll"
                                        v-model="item.Focus">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field label="Years"
                                          v-model="item.Years"
                                          v-mask="masks.yearRange"
                                          :rules="rules.Year"
                                          :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Fellowships
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="fellowships"
                                v-model="model.Fellowships"
                                :model="getHistoryModel()"
                                :disabled="disableAll">
                        <template v-slot:item="{ item, index }">
                          <v-col cols="12">
                            <v-autocomplete v-model="item.Institute"
                                            label="Institute"
                                            item-text="Title"
                                            item-value="Id"
                                            return-object
                                            :items="places.items"
                                            :disabled="disableAll"
                                            @keydown.enter.native.prevent="">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                              <template v-slot:prepend-item>
                                <v-list-item v-if="$store.getters.can('Editor')"
                                             ripple
                                             @click="openPlaceCreator(
                                           'Fellowships', 'Institute', index
                                         )">
                                  <v-list-item-icon>
                                    <v-icon>add</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    Create New...
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                              </template>
                              <template v-slot:item="data">
                                <v-list-item-content>
                                  <v-list-item-title>
                                    {{ data.item.Title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    {{ getAddress(data.item) }}
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-combobox label="Specialty"
                                        :items="tags.items.Fellowship"
                                        :disabled="disableAll"
                                        v-model="item.Focus">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field label="Years"
                                          v-model="item.Years"
                                          v-mask="masks.yearRange"
                                          :rules="rules.Year"
                                          :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Internships
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="internships"
                                v-model="model.Internships"
                                :model="getHistoryModel()"
                                :disabled="disableAll">
                        <template v-slot:item="{ item, index }">
                          <v-col cols="12">
                            <v-autocomplete v-model="item.Institute"
                                            label="Institute"
                                            item-text="Title"
                                            item-value="Id"
                                            return-object
                                            :items="places.items"
                                            :disabled="disableAll"
                                            @keydown.enter.native.prevent="">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                              <template v-slot:prepend-item>
                                <v-list-item v-if="$store.getters.can('Editor')"
                                             ripple
                                             @click="openPlaceCreator(
                                           'Internships', 'Institute', index
                                         )">
                                  <v-list-item-icon>
                                    <v-icon>add</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    Create New...
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                              </template>
                              <template v-slot:item="data">
                                <v-list-item-content>
                                  <v-list-item-title>
                                    {{ data.item.Title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    {{ getAddress(data.item) }}
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-combobox label="Specialty"
                                        :items="tags.items.Internship"
                                        :disabled="disableAll"
                                        v-model="item.Focus">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field label="Years"
                                          v-model="item.Years"
                                          v-mask="masks.yearRange"
                                          :rules="rules.Year"
                                          :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Residencies
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="residencies"
                                v-model="model.Residencies"
                                :model="getHistoryModel()"
                                :disabled="disableAll">
                        <template v-slot:item="{ item, index }">
                          <v-col cols="12">
                            <v-autocomplete v-model="item.Institute"
                                            label="Institute"
                                            item-text="Title"
                                            item-value="Id"
                                            return-object
                                            :items="places.items"
                                            :disabled="disableAll"
                                            @keydown.enter.native.prevent="">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                              <template v-slot:prepend-item>
                                <v-list-item v-if="$store.getters.can('Editor')"
                                             ripple
                                             @click="openPlaceCreator(
                                           'Residencies', 'Institute', index
                                         )">
                                  <v-list-item-icon>
                                    <v-icon>add</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    Create New...
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider />
                              </template>
                              <template v-slot:item="data">
                                <v-list-item-content>
                                  <v-list-item-title>
                                    {{ data.item.Title }}
                                  </v-list-item-title>
                                  <v-list-item-subtitle>
                                    {{ getAddress(data.item) }}
                                  </v-list-item-subtitle>
                                </v-list-item-content>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-combobox label="Specialty"
                                        :items="tags.items.Residency"
                                        :disabled="disableAll"
                                        v-model="item.Focus">
                              <template v-slot:append>
                                <v-btn color="accent" text small>
                                  SELECT <v-icon dense right>add</v-icon>
                                </v-btn>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field label="Years"
                                          v-model="item.Years"
                                          v-mask="masks.yearRange"
                                          :rules="rules.Year"
                                          :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      Publications
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="publications"
                                v-model="model.Publications"
                                :disabled="disableAll">
                        <template v-slot:item="{ index }">
                          <v-col cols="12">
                            <v-textarea label="Title"
                                        v-model="model.Publications[index]"
                                        outlined
                                        clearable
                                        :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      External Links
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <Repeater id="external-links"
                                v-model="model.ExternalLinks"
                                :model="{ Url: '', Title: '' }"
                                :disabled="disableAll">
                        <template v-slot:item="{ item }">
                          <v-col cols="12">
                            <v-text-field label="URL"
                                          v-model="item.Url"
                                          :disabled="disableAll" />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field label="Title"
                                          v-model="item.Title"
                                          :disabled="disableAll" />
                          </v-col>
                        </template>
                      </Repeater>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Submit -->
            <v-row v-if="$store.getters.can('Editor')">
              <v-col cols="12">
                <h2>
                  Provider Visibility
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon class="ml-2" v-on="on">help</v-icon>
                    </template>
                    <ul>
                      <li>Draft: New Provider Received or In Progress</li>
                      <li>Submitted: Profile Complete. Awaiting Approval</li>
                      <li>Published: Available on Carle.org</li>
                    </ul>
                  </v-tooltip>
                </h2>
              </v-col>
              <v-col cols="12" md="auto">
                <v-select class="select-status"
                          label="Status"
                          v-model="model.Status"
                          outlined
                          :disabled="disableAll"
                          :items="statusOptions" />
              </v-col>
              <v-col cols="12" md="auto">
                <v-switch v-model="model.Active"
                          :disabled="disableAll">
                  <template v-slot:label>
                    {{ model.Active ? 'Active' : 'Inactive' }}
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon class="ml-2" dense v-on="on">help</v-icon>
                      </template>
                      <ul>
                        <li>
                          Active: Provider is an active member of the Carle Network
                        </li>
                        <li>
                          Inactive: Provider is no longer an active member of the Carle Network
                        </li>
                      </ul>
                    </v-tooltip>
                  </template>
                </v-switch>
              </v-col>
            </v-row>
            <v-row v-if="$store.getters.can('Editor')">
              <v-col cols="auto">
                <v-btn type="submit" color="primary" :loading="loading">
                  {{ id ? 'Save' : 'Create' }}
                </v-btn>
              </v-col>
              <v-col cols="auto" v-if="id">
                <v-btn outlined color="primary"
                       target="_blank"
                       :href="previewLink">Preview</v-btn>
              </v-col>
            </v-row>
            <v-row v-else-if="!disableAll">
              <v-col cols="auto">
                <v-btn outlined color="primary" type="submit">Save Draft</v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn color="primary" @click="$refs.confirm.open()">Submit for Review</v-btn>
              </v-col>
              <v-col cols="auto" v-if="id">
                <v-btn outlined
                       color="primary"
                       target="_blank"
                       :href="previewLink">Preview</v-btn>
              </v-col>
            </v-row>
            <v-row v-if="$store.getters.can('Editor') && this.id">
              <v-col cols="12">
                <h2>
                  Provider Start/End Date Scheduling
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon class="ml-2" v-on="on">help</v-icon>
                    </template>
                    Here you can scheduling the start date or end date of provider profiles.
                  </v-tooltip>
                </h2>
                <p v-if="this.ActivateDatetime">
                  <span v-if="this.Active && !this.HangfireId">
                    Provider was activated on
                    {{ formatPrettyDate(this.ActivatedOn) }}.
                  </span>
                  <span v-if="!this.Active && !this.HangfireId">
                    Provider was deactivated on
                    {{ formatPrettyDate(this.ActivatedOn) }}.
                  </span>
                  <span v-if="this.HangfireId && this.HangfireId !== 0 && this.Active">
                    Provider is currently scheduled to deactivate on
                    {{ formatPrettyDate(this.ActivateDatetime) }}
                  </span>
                  <span v-if="this.HangfireId && this.HangfireId !== 0 && !this.Active">
                    Provider is currently scheduled to activate on
                    {{ formatPrettyDate(this.ActivateDatetime) }}
                  </span>
                </p>
              </v-col>
            </v-row>
            <v-row v-if="$store.getters.can('Editor') && this.id">
              <v-col cols="4">
                <v-datetime-picker label="Select Date & Time" date-format="yyyy-MM-dd"
                time-format="HH:mm:ss.s" v-model="model.ActivateDatetime">
                </v-datetime-picker>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="scheduleActivate" v-if="this.Active"
                color="primary" :loading="loading">
                  Schedule End-date
                </v-btn>
                <v-btn @click="scheduleActivate" v-if="!this.Active"
                color="primary" :loading="loading">
                  Schedule Start-date
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn v-if="this.HangfireId && this.HangfireId !== 0
                && this.Active"
                @click="cancelSchedule"
                outlined color="primary" :loading="loading">
                  Cancel End-date
                </v-btn>
                <v-btn v-if="this.HangfireId && this.HangfireId !== 0
                && !this.Active"
                @click="cancelSchedule"
                outlined color="primary" :loading="loading">
                  Cancel Start-date
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
      <NestedForm form="PlaceForm"
                  title="Create Place"
                  ref="PlaceForm"
                  v-if="$store.getters.can('Editor')"
                  @update="addNewPlace" />
      <v-row justify="center" align="center">
        <ConfirmDialog ref="confirm"
                       title="WARNING"
                       width="450"
                       @continue="saveAndSubmit">
          You are about to submit this Provider for review.<br>
          This form will be put into readonly mode pending approval from an Editor or Administrator.
          <br><br>
          <strong>Are you sure you would like to proceed?</strong>
        </ConfirmDialog>
      </v-row>
    </v-col>
  </v-row>
</template>

<style lang="scss">
  .v-chip{
    &__content .text-wrapped {
      white-space: nowrap;
      overflow: hidden;

      &--ellipsis{
        text-overflow: ellipsis;
      }
    }
  }
</style>

<script>
import { mask } from 'vue-the-mask';
import FormMixins from '@/components/Mixins/Forms';
import draggable from 'vuedraggable';
import { format, parseISO } from 'date-fns';
import {
  rules,
  getMediaUrl,
  generateProviderPdf,
  arrayToObject,
} from '@/util/Helpers';

export default {
  name: 'ProviderForm',
  directives: {
    mask,
  },
  components: {
    draggable,
  },
  mixins: [FormMixins],
  props: {
    Id: [String, Number],
    FirstName: String,
    PrefName: String,
    LastName: String,
    Title: String,
    YearJoined: String,
    PhotoId: [String, Number],
    PdfId: [String, Number],
    ProviderCode: String,
    Npi: String,
    VimeoId: String,
    Comments: String,
    HasStars: Boolean,
    NoLocations: Boolean,
    IndependentPractitioner: Boolean,
    Resident: Boolean,
    ActivatedOn: [String, Date],
    HangfireId: [String, Number],
    ActivateDatetime: {
      type: [Date, String],
      default: null,
    },
    Searchable: Boolean,
    Locations: {
      type: Array,
      default: () => [],
    },
    Affiliations: {
      type: Array,
      default: () => [],
    },
    Certifications: {
      type: Array,
      default: () => [],
    },
    Departments: {
      type: Array,
      default: () => [],
    },
    MedicalInterests: {
      type: Array,
      default: () => [],
    },
    Publications: {
      type: Array,
      default: () => [],
    },
    ExternalLinks: {
      type: Array,
      default: () => [],
    },
    ResearchDepartments: {
      type: Array,
      default: () => [],
    },
    ResearchInterests: {
      type: Array,
      default: () => [],
    },
    Specialties: {
      type: Array,
      default: () => [],
    },
    Languages: {
      type: Array,
      default: () => [],
    },
    Pronouns: {
      type: Array,
      default: () => [],
    },
    Education: {
      type: Array,
      default: () => [],
    },
    Fellowships: {
      type: Array,
      default: () => [],
    },
    Internships: {
      type: Array,
      default: () => [],
    },
    Residencies: {
      type: Array,
      default: () => [],
    },
    AdditionalRecognitions: {
      type: Array,
      default: () => [],
    },
    FacultyAppts: {
      type: Array,
      default: () => [],
    },

    Active: Boolean,
    Status: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      id: this.Id,
      model: {
        FirstName: this.FirstName,
        PrefName: this.PrefName,
        LastName: this.LastName,
        Title: this.Title,
        YearJoined: this.YearJoined,
        ProviderCode: this.ProviderCode,
        Npi: this.Npi,
        VimeoId: this.VimeoId,
        Comments: this.Comments,
        PhotoId: this.PhotoId,
        PdfId: this.PdfId || null,
        HasStars: this.HasStars,
        NoLocations: this.NoLocations,
        IndependentPractitioner: this.IndependentPractitioner,
        Resident: this.Resident,
        Searchable: this.Searchable,
        Locations: this.Locations,
        Affiliations: this.Affiliations,
        AdditionalRecognitions: this.AdditionalRecognitions,
        Certifications: this.Certifications,
        Departments: this.Departments,
        MedicalInterests: this.MedicalInterests,
        Publications: this.Publications,
        ExternalLinks: this.ExternalLinks,
        ResearchDepartments: this.ResearchDepartments,
        ResearchInterests: this.ResearchInterests,
        Specialties: this.Specialties,
        Languages: this.Languages,
        Pronouns: this.Pronouns,
        Education: this.Education,
        Fellowships: this.Fellowships,
        Internships: this.Internships,
        Residencies: this.Residencies,
        ActivatedOn: this.ActivatedOn,
        Active: this.Active,
        Status: this.Status,
        ActivateDatetime: new Date(this.ActivateDatetime),
        HangfireId: this.HangfireId,
        AdditionalRecognitionsSingle: this.AdditionalRecognitions.join(','),
        FacultyAppts: this.FacultyAppts,

      },
      rules: {
        FirstName: [
          (v) => rules.required(v, 'Please enter the Provider\'s First Name'),
        ],
        LastName: [
          (v) => rules.required(v, 'Please enter the Provider\'s Last Name'),
        ],
        Title: [
          (v) => rules.required(v, 'Please enter the Provider\'s Title'),
        ],
        Year: [
          (v) => rules.pattern(v, /^[\d]{4}(-[\d]{4})?$/, 'Please enter a valid year or year range'),
        ],
      },
      loading: false,
      pdfLoading: false,
      statusOptions: [
        { value: 0, text: 'Draft' },
        { value: 1, text: 'Submitted' },
        { value: 2, text: 'Published' },
      ],
      tags: {
        loading: true,
        items: [],
      },
      places: {
        loading: true,
        items: [],
        tmp: null,
      },
      locations: {
        loading: true,
        items: [],
        selected: this.Locations.map((l) => ({
          Id: l.Id,
          Title: l.Title,
          Phone: l.Phone,
        })),
        highlighted: { id: null, index: null },
        primary: this.Locations.length ? this.Locations[0].Id : null,
      },
      masks: {
        phone: '(###) ###-####',
        yearRange: '####-####',
      },
      dragged: {
        from: -1,
        to: -1,
        newIndex: -1,
        element: '',
        list: [],
      },
    };
  },
  watch: {
    'locations.selected': {
      handler(newVal) {
        if (newVal.length === 1) {
          this.locations.primary = newVal[0].Id;
        }

        const keyedLocations = arrayToObject(this.locations.items, 'Id');
        const keyedLocationsModel = arrayToObject(this.model.Locations, 'Id');
        this.model.Locations = newVal.map((l) => ({
          Id: l.Id,
          Title: l.Title,
          IsPrimary: l.Id === this.locations.primary,
          Phone: keyedLocationsModel[l.Id]
            ? keyedLocationsModel[l.Id].Phone
            : keyedLocations[l.Id].Phone,
        }));
      },
    },
    'locations.primary': {
      handler(newVal) {
        this.model.Locations.forEach((l, i) => {
          this.model.Locations[i].IsPrimary = l.Id === newVal;
        });
      },
    },
  },
  computed: {
    disableAll() {
      return !this.canEdit();
    },
    previewLink() {
      const token = this.$store.getters.user.access_token;
      return `${process.env.VUE_APP_PREVIEW}/Find-a-Doctor/${this.id}?preview=${token}`;
    },
    dragOptionsChips() {
      return {
        animation: 200,
        group: 'group',
        disabled: false,
        ghostClass: 'ghost',
        sort: true,
      };
    },
  },
  methods: {
    formatPrettyDate(date) {
      return format(parseISO(date), 'MMMM dd, yyyy hh:mm aaaa (zzzz)');
    },
    async submit() {
      this.model.AdditionalRecognitions = this.model.AdditionalRecognitionsSingle.split(',');
      if (this.$refs.crudForm.validate()) {
        this.loading = true;
        if (this.id) {
          await this.update();
        } else {
          await this.create();
        }
        this.loading = false;
      } else {
        this.$refs.crudForm.inputs.every(this.goToFirstError);
      }
    },
    saveAndSubmit() {
      this.model.Status = 1;
      this.submit();
    },
    async create() {
      try {
        const response = await this.$store.dispatch('post', {
          api: 'Provider',
          data: this.model,
        });
        this.$emit('snackbar', 'Provider successfully created!');
        this.id = response.data.Id;
      } catch (e) {
        // Do nothing
      }
    },
    async update() {
      try {
        const pdf = await generateProviderPdf(this.id, this.model);
        this.model.PdfId = pdf.data[0].Id;
        await this.$store.dispatch('put', {
          api: `Provider/${this.id}`,
          data: this.model,
        });
        this.$emit('snackbar', 'Provider successfully updated!');
      } catch (e) {
        // Do nothing0
        // console.log('update?', e);
      }
    },
    async scheduleActivate() {
      try {
        if (this.Active === false) {
          await this.$store.dispatch('put', {
            api: `Provider/${this.id}/Activate/Schedule?date=${format(this.model.ActivateDatetime, 'yyyy-MM-dd HH:mm:ss.s')}`,
            data: this.model,
          });
        } else {
          await this.$store.dispatch('put', {
            api: `Provider/${this.id}/Deactivate/Schedule?date=${format(this.model.ActivateDatetime, 'yyyy-MM-dd HH:mm:ss.s')}`,
            data: this.model,
          });
        }
        this.$emit('snackbar', 'Provider start/end date scheduled successfully.');
      } catch (e) {
        // Do nothing
      }
    },
    async cancelSchedule() {
      try {
        if (this.HangfireId !== '') {
          await this.$store.dispatch('put', {
            api: `Provider/${this.id}/${this.HangfireId}/Activate/CancelSchedule`,
            data: this.model,
          });
        }
      } catch (e) {
        // Do nothing
      }
    },
    canEdit() {
      return this.$store.getters.can('Editor') || this.model.Status === 0;
    },
    getHistoryModel() {
      return { Institute: { Title: '', Id: null }, Focus: '', Years: '' };
    },
    mediaUrl(id, directory = '', notfound = null) {
      return getMediaUrl(id, directory, notfound);
    },
    openPlaceCreator(item, prop, index) {
      this.$refs.PlaceForm.open();
      this.places.tmp = { item, prop, index };
    },
    addNewPlace(response) {
      this.places.items.push(response);

      const { item, prop, index } = this.places.tmp;
      this.model[item][index][prop] = response;
      this.places.tmp = null;
    },
    getAddress(place) {
      return [
        `${place.Address1 || ''} ${place.Address2 || ''}`.trim(),
        `${place.City || ''} ${place.State || ''}`.trim(),
        place.Zip,
        place.Country,
      ].filter((a) => !!a).join(', ');
    },
    highlightLocation(item) {
      this.locations.highlighted = item;
      this.$refs.locationsMenu.blur();
    },
    removeLocation(item) {
      const i = this.locations.selected.findIndex((l) => l.Id === item.Id);
      this.$delete(this.locations.selected, i);
      if (this.locations.primary === item.Id) {
        this.locations.primary = this.locations.selected.length
          ? this.locations.selected[0].Id
          : null;
      }
    },
    removeItem(set, item) {
      const i = set.indexOf(item);
      this.$delete(set, i);
    },
    moveToFront(set, item) {
      this.removeItem(set, item);
      set.unshift(item);
    },
    move(value) {
      // console.log('move', value);
      const fromPos = parseInt(value.from.id, 10);
      const element = value.relatedContext.list[fromPos];
      this.dragged = {
        from: fromPos,
        to: parseInt(value.to.id, 10),
        newIndex: value.draggedContext.futureIndex,
        element,
        list: value.relatedContext.list.filter((item) => item !== element),
      };
    },
    changeOrder(set) {
      // console.log('set', set);
      // console.log('dragged', this.dragged);
      const origList = this.dragged.list.filter((item) => item !== this.dragged.element);
      // console.log('changeAffiliation->origList', origList);
      origList.splice(this.dragged.to, 0, this.dragged.element);
      // console.log('Re-ordered', origList);
      set.splice(0, set.length);
      origList.forEach((item) => set.push(item));
    },
  },
  created() {
    this.$store.dispatch('get', 'Tag/Provider').then((response) => {
      this.tags.items = response.data;
    }).finally(() => {
      this.tags.loading = false;
    });

    this.$store.dispatch('get', 'Place').then((response) => {
      this.places.items = response.data;
    }).finally(() => {
      this.places.loading = false;
    });

    this.$store.dispatch('get', { api: 'Location', params: { fields: 'Id,Title,Phone' } }).then((response) => {
      this.locations.items = response.data.Items;
    }).finally(() => {
      this.locations.loading = false;
    });
  },
  mounted() {
    if (!this.canEdit()) {
      const heading = this.model.Status === 1
        ? 'Under Review'
        : 'Readonly Mode';
      const msg = this.model.Status === 1
        ? 'This Provider is currently under review. No changes may be made at this time.'
        : 'This Provider has been approved and published. Please see an Editor or Administrator if you would like to make any changes.';
      this.$emit('alert', heading, msg);
    }
  },
};
</script>
